import React, { useState, useEffect } from 'react';
import { getViewStats } from '../lib/supabase';
import { format } from 'date-fns';
import { FiEye, FiClock, FiUser } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const RecentActivity = ({ profileId }) => {
  const [recentViews, setRecentViews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentActivity();
  }, [profileId]);

  const loadRecentActivity = async () => {
    try {
      const { data: views } = await getViewStats(profileId);
      if (views) {
        // Get the 10 most recent views
        const recent = views
          .sort((a, b) => new Date(b.viewed_at) - new Date(a.viewed_at))
          .slice(0, 10)
          .map(view => ({
            id: view.id,
            type: 'view',
            message: view.viewer_wallet
              ? `Profile viewed by ${view.viewer_wallet.slice(0, 6)}...${view.viewer_wallet.slice(-4)}`
              : 'Anonymous profile view',
            timestamp: view.viewed_at,
            wallet: view.viewer_wallet,
          }));
        setRecentViews(recent);
      }
    } catch (error) {
      console.error('Error loading recent activity:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="glass-dark rounded-xl border border-dark-700 p-6">
        <h2 className="text-lg font-semibold text-lime-bright mb-6">Recent Activity</h2>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-dark-700 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-dark-700 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-dark-700 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass-dark rounded-xl border border-dark-700 p-6">
      <h2 className="text-lg font-semibold text-lime-bright mb-6">Recent Activity</h2>
      {recentViews.length === 0 ? (
        <div className="text-center py-8">
          <SafeIcon icon={FiEye} className="text-dark-500 text-3xl mb-4 mx-auto" />
          <p className="text-dark-300">No recent activity yet</p>
          <p className="text-sm text-dark-500 mt-2">
            Activity will appear here when people visit your profile
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {recentViews.map((activity) => (
            <div key={activity.id} className="flex items-start space-x-3">
              <div className="w-8 h-8 bg-lime-500/20 rounded-full flex items-center justify-center flex-shrink-0 border border-lime-500/50">
                <SafeIcon icon={activity.wallet ? FiUser : FiEye} className="text-lime-400 text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-dark-100 mb-1">
                  {activity.message}
                </p>
                <div className="flex items-center text-xs text-dark-400">
                  <SafeIcon icon={FiClock} className="mr-1" />
                  {format(new Date(activity.timestamp), 'MMM d, h:mm a')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentActivity;